import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiClient } from '../api/client';
import { useApi } from './provider';

export const tagKeys = {
  byWorkspace: (workspaceSlug: string) => ['tags', 'workspace', workspaceSlug] as const,
};

export const tagsQueryOptions = (api: ApiClient, workspaceSlug: string) =>
  queryOptions({
    queryKey: tagKeys.byWorkspace(workspaceSlug),
    // Tags are workspace-level reference data — added rarely, used
    // on every task render. Mutations (create/update/delete tag)
    // explicitly invalidate this key.
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/workspaces/{workspace_slug}/tags', {
        params: { path: { workspace_slug: workspaceSlug } },
      });
      if (error) throw error;
      return data;
    },
  });

export const useTags = (workspaceSlug: string | undefined) => {
  const api = useApi();
  return useQuery({
    ...tagsQueryOptions(api, workspaceSlug ?? ''),
    enabled: Boolean(workspaceSlug),
  });
};

export interface CreateTagInput {
  name: string;
  color: string;
}

export const useCreateTag = (workspaceSlug: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateTagInput) => {
      const { data, error } = await api.POST('/v1/workspaces/{workspace_slug}/tags', {
        params: { path: { workspace_slug: workspaceSlug } },
        body: input,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: tagKeys.byWorkspace(workspaceSlug) });
    },
  });
};

export interface UpdateTagInput {
  name?: string;
  color?: string;
}

export const useUpdateTag = (workspaceSlug: string, tagId: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateTagInput) => {
      const { data, error } = await api.PATCH('/v1/workspaces/{workspace_slug}/tags/{tagId}', {
        params: { path: { workspace_slug: workspaceSlug, tagId } },
        body: input,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: tagKeys.byWorkspace(workspaceSlug) });
      // Tag rename/recolor affects task views that render tag chips by id.
      await qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
};

export const useDeleteTag = (workspaceSlug: string) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (tagId: string) => {
      const { error } = await api.DELETE('/v1/workspaces/{workspace_slug}/tags/{tagId}', {
        params: { path: { workspace_slug: workspaceSlug, tagId } },
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: tagKeys.byWorkspace(workspaceSlug) });
      // Cascade detached the tag from tasks; refetch task lists.
      await qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
};
